class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentSearchesKey = 'recentSearches';
    this.recentSearchesLimit = 3;

    this.setupEventListeners();
    this.renderRecentSearches();
    this.updateGenderRecommendations();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );

    this.addEventListener(
      'click',
      this.onSearchHubClick.bind(this)
    );

    document.addEventListener(
      'click',
      event => {
        const genderButton = event.target.closest('[data-select-gender]');

        if (!genderButton) return;

        requestAnimationFrame(() => {
          this.updateGenderRecommendations();
          this.refreshGenderResults();
        });
      }
    );

    if (document.body) {
      new MutationObserver(() => {
        this.updateGenderRecommendations();
        this.refreshGenderResults();
      }).observe(document.body, {
        attributes: true,
        attributeFilter: ['data-gender']
      });
    }

    const searchDetails = this.closest('details');

    if (searchDetails) {
      searchDetails.addEventListener(
        'toggle',
        () => {
          if (!searchDetails.open) return;

          this.renderRecentSearches();
          this.updateGenderRecommendations();
          this.openSearchHub();
        }
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  normalizeSearchTerm(value = '') {
    return String(value)
      .normalize('NFC')
      .toLocaleLowerCase('es-MX')
      .trim()
      .replace(/\s+/g, ' ');
  }

  resolvedGender() {
    if (window.GenderProducts && typeof GenderProducts.currentGender === 'function') {
      return GenderProducts.currentGender();
    }

    let storedGender = '';

    try {
      storedGender = sessionStorage.getItem('selectedGender') || '';
    } catch (error) {
      storedGender = '';
    }

    const fromStore = this.canonicalGender(storedGender);
    if (fromStore === 'hombre' || fromStore === 'mujer') return fromStore;

    const fromBody = document.body && document.body.getAttribute('data-gender');
    const fromRoot = document.documentElement && document.documentElement.getAttribute('data-gender');
    const fromDom = this.canonicalGender(fromBody || fromRoot);
    if (fromDom === 'hombre' || fromDom === 'mujer') return fromDom;

    return 'mujer';
  }

  getSearchCacheKey(value = '') {
    const gender = this.resolvedGender();

    return encodeURIComponent(
      this.normalizeSearchTerm(value)
    ) + '|' + gender;
  }

  refreshGenderResults() {
    this.cachedResults = {};

    const query = this.getQuery();

    if (!query.length) return;

    this.getSearchResults(query);
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    const previousNormalized = this.normalizeSearchTerm(
      this.searchTerm
    );

    const newNormalized = this.normalizeSearchTerm(
      newSearchTerm
    );

    if (
      !previousNormalized ||
      !newNormalized.startsWith(previousNormalized)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.updateSearchForTerm(
      this.searchTerm,
      newSearchTerm
    );

    this.searchTerm = newSearchTerm;

    this.renderRecentSearches(true);

    if (!this.searchTerm.length) {
      this.clearPredictiveResults();
      this.renderRecentSearches();
      this.openSearchHub();
      return;
    }

    this.getSearchResults(
      this.searchTerm
    );
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    const selectedLink = this.querySelector(
      '#predictive-search-results [aria-selected="true"] a'
    );

    if (!query.length) {
      event.preventDefault();
      return;
    }

    if (selectedLink) {
      event.preventDefault();
      return;
    }

    this.saveRecentSearch(
      query
    );
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortController.abort();
      this.abortController = new AbortController();

      this.clearPredictiveResults();
      this.renderRecentSearches();
      this.openSearchHub();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderRecentSearches();
      this.updateGenderRecommendations();
      this.openSearchHub();
      return;
    }

    if (
      this.searchTerm !== currentSearchTerm
    ) {
      this.onChange();
    } else if (
      this.getAttribute('results') === 'true'
    ) {
      this.open();
    } else {
      this.getSearchResults(
        this.searchTerm
      );
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        !this.contains(
          document.activeElement
        )
      ) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clearPredictiveResults();
      this.renderRecentSearches();
      this.openSearchHub();
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  onSearchHubClick(event) {
    const recentSearch = event.target.closest(
      '[data-recent-search]'
    );

    if (!recentSearch) return;

    event.preventDefault();

    const query = recentSearch.dataset.recentSearch;

    if (!query) return;

    this.input.value = query;
    this.searchTerm = '';
    this.input.focus();
    this.onChange();
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(
        sessionStorage.getItem(
          this.recentSearchesKey
        )
      );

      return Array.isArray(searches)
        ? searches
        : [];
    } catch (error) {
      console.error(
        'Could not read recent searches:',
        error
      );

      return [];
    }
  }

  saveRecentSearch(query) {
    const cleanQuery = query.trim();

    if (!cleanQuery) return;

    const normalizedQuery = this.normalizeSearchTerm(
      cleanQuery
    );

    let searches = this.getRecentSearches();

    searches = searches.filter(
      search => {
        return (
          this.normalizeSearchTerm(
            search
          ) !== normalizedQuery
        );
      }
    );

    searches.unshift(
      cleanQuery
    );

    searches = searches.slice(
      0,
      this.recentSearchesLimit
    );

    sessionStorage.setItem(
      this.recentSearchesKey,
      JSON.stringify(searches)
    );

    this.allPredictiveSearchInstances.forEach(
      instance => {
        instance.renderRecentSearches?.();
      }
    );
  }

  renderRecentSearches(
    excludeCurrentQuery = false
  ) {
    const wrapper = this.querySelector(
      '[data-recent-searches]'
    );

    const list = this.querySelector(
      '[data-recent-searches-list]'
    );

    if (
      !wrapper ||
      !list
    ) {
      return;
    }

    let searches = this.getRecentSearches();

    if (excludeCurrentQuery) {
      const currentQuery = this.normalizeSearchTerm(
        this.getQuery()
      );

      if (currentQuery) {
        searches = searches.filter(
          search => {
            return (
              this.normalizeSearchTerm(
                search
              ) !== currentQuery
            );
          }
        );
      }
    }

    list.innerHTML = '';

    if (!searches.length) {
      wrapper.hidden = true;
      return;
    }

    searches.forEach(
      (search, index) => {
        const button = document.createElement(
          'button'
        );

        button.type = 'button';
        button.className = 'search-modal__recent-search';
        button.dataset.recentSearch = search;
        button.id = `RecentSearch-${index + 1}`;
        button.textContent = search;

        list.appendChild(
          button
        );
      }
    );

    wrapper.hidden = false;
  }

  canonicalGender(value) {
    const text = String(value || '').trim().toLowerCase();

    if (
      text === 'hombre' ||
      text === 'men' ||
      text === 'gender_hombre' ||
      text === 'gender-hombre'
    ) {
      return 'hombre';
    }

    if (
      text === 'mujer' ||
      text === 'women' ||
      text === 'gender_mujer' ||
      text === 'gender-mujer'
    ) {
      return 'mujer';
    }

    return text;
  }

  updateGenderRecommendations() {
    const selectedGender = this.resolvedGender();

    const recommendationsWrapper = this.querySelector(
      '[data-search-recommendations]'
    );

    const recommendations = this.querySelectorAll(
      '[data-recommendations-gender]'
    );

    if (!recommendationsWrapper) return;

    if (!recommendations.length) {
      recommendationsWrapper.hidden = true;
      return;
    }

    let hasVisibleRecommendations = false;

    recommendations.forEach(
      list => {
        const listGender = list.getAttribute(
          'data-recommendations-gender'
        );

        const shouldShow =
          this.canonicalGender(listGender) === selectedGender &&
          selectedGender !== '';

        list.hidden = !shouldShow;

        if (
          shouldShow &&
          list.children.length > 0
        ) {
          hasVisibleRecommendations = true;
        }
      }
    );

    recommendationsWrapper.hidden =
      !hasVisibleRecommendations;
  }

  updateSearchForTerm(
    previousTerm,
    newTerm
  ) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );

    const currentButtonText =
      searchForTextElement?.innerText;

    if (!currentButtonText) return;

    const escapedPreviousTerm =
      previousTerm.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );

    if (escapedPreviousTerm) {
      const matches =
        currentButtonText.match(
          new RegExp(
            escapedPreviousTerm,
            'gu'
          )
        ) || [];

      if (matches.length > 1) {
        return;
      }
    }

    const newButtonText =
      currentButtonText.replace(
        previousTerm,
        newTerm
      );

    searchForTextElement.innerText =
      newButtonText;
  }

  switchOption(direction) {
    if (
      !this.getAttribute('open')
    ) {
      return;
    }

    const moveUp =
      direction === 'up';

    const selectedElement =
      this.querySelector(
        '#predictive-search-results [aria-selected="true"]'
      );

    const allVisibleElements =
      Array.from(
        this.querySelectorAll(
          '#predictive-search-results li, #predictive-search-results button.predictive-search__item'
        )
      ).filter(
        element =>
          element.offsetParent !== null
      );

    if (!allVisibleElements.length) {
      return;
    }

    let activeElementIndex = 0;

    if (
      moveUp &&
      !selectedElement
    ) {
      return;
    }

    let selectedElementIndex = -1;
    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i < allVisibleElements.length
    ) {
      if (
        allVisibleElements[i] ===
        selectedElement
      ) {
        selectedElementIndex = i;
      }

      i++;
    }

    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (
      !moveUp &&
      selectedElement
    ) {
      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex ===
      selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[
        activeElementIndex
      ];

    activeElement.setAttribute(
      'aria-selected',
      true
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption =
      this.querySelector(
        '#predictive-search-results [aria-selected="true"] a, #predictive-search-results button[aria-selected="true"]'
      );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  sectionInnerHtml(text) {
    if (!text) return '';

    const parsed =
      new DOMParser()
        .parseFromString(
          text,
          'text/html'
        );

    const section =
      parsed.querySelector(
        '#shopify-section-predictive-search'
      );

    return section
      ? section.innerHTML
      : '';
  }

  mergeGenderResults(suggestText, productText) {
    const suggestHtml =
      this.sectionInnerHtml(
        suggestText
      );

    const productHtml =
      this.sectionInnerHtml(
        productText
      );

    const holder =
      document.createElement(
        'div'
      );

    holder.innerHTML =
      suggestHtml || productHtml;

    if (!productHtml) {
      return holder.innerHTML;
    }

    const productHolder =
      document.createElement(
        'div'
      );

    productHolder.innerHTML =
      productHtml;

    const products =
      productHolder.querySelector(
        '[data-predictive-search-products]'
      );

    if (!products) {
      return holder.innerHTML;
    }

    if (!suggestHtml) {
      return productHtml;
    }

    let groups =
      holder.querySelector(
        '#predictive-search-results-groups-wrapper'
      );

    const results =
      holder.querySelector(
        '#predictive-search-results'
      ) || holder;

    if (!groups) {
      groups =
        document.createElement(
          'div'
        );

      groups.id =
        'predictive-search-results-groups-wrapper';

      groups.className =
        'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';

      results.insertBefore(
        groups,
        results.firstChild
      );
    }

    const group =
      document.createElement(
        'div'
      );

    group.className =
      'predictive-search__result-group';

    group.appendChild(products);
    groups.appendChild(group);
    groups.classList.remove(
      'predictive-search__results-groups-wrapper--no-products'
    );

    return holder.innerHTML;
  }

  getSearchResults(searchTerm) {
    const queryKey =
      this.getSearchCacheKey(
        searchTerm
      );

    this.setLiveRegionLoadingState();

    if (
      this.cachedResults[
        queryKey
      ]
    ) {
      this.renderSearchResults(
        this.cachedResults[
          queryKey
        ]
      );

      const searchDeferred =
        this.dispatchSearchUpdateEvent(
          searchTerm
        );

      searchDeferred?.resolve({
        totalCount:
          this.getTotalResultCount()
      });

      return;
    }

    const searchDeferred =
      this.dispatchSearchUpdateEvent(
        searchTerm
      );

    this.abortController.abort();
    this.abortController =
      new AbortController();

    const signal =
      this.abortController.signal;

    const routesRoot = window.Shopify
      && Shopify.routes
      && Shopify.routes.root
      ? Shopify.routes.root
      : '/';

    const root = routesRoot.endsWith('/')
      ? routesRoot
      : `${routesRoot}/`;

    const tag = window.GenderProducts
      ? GenderProducts.tagName()
      : 'Gender_Mujer';

    const constrained =
      `tag:${tag} AND ${searchTerm}`;

    const productUrl =
      `${root}search?section_id=predictive-search&type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(constrained)}`;

    const suggestUrl =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=query,collection,page,article&resources[limit]=4&section_id=predictive-search`;

    const readSection = response => {
      if (!response.ok) {
        throw new Error(
          response.status
        );
      }

      return response.text();
    };

    Promise.all([
      fetch(suggestUrl, { signal }).then(readSection),
      fetch(productUrl, { signal }).then(readSection)
    ])
      .then(
        ([suggestText, productText]) => {
          const resultsMarkup =
            this.mergeGenderResults(
              suggestText,
              productText
            );

          if (!resultsMarkup) {
            throw new Error(
              'Predictive search section was not returned.'
            );
          }

          this.allPredictiveSearchInstances.forEach(
            predictiveSearchInstance => {
              predictiveSearchInstance.cachedResults[
                queryKey
              ] = resultsMarkup;
            }
          );

          this.renderSearchResults(
            resultsMarkup
          );

          searchDeferred?.resolve({
            totalCount:
              this.getTotalResultCount()
          });
        }
      )
      .catch(
        error => {
          if (
            error?.name === 'AbortError' ||
            error?.code === 20
          ) {
            searchDeferred?.reject(
              error
            );

            return;
          }

          searchDeferred?.reject(
            error
          );

          console.error(
            'Predictive search error:',
            error
          );

          this.clearPredictiveResults();
        }
      );
  }

  getTotalResultCount() {
    return (
      parseInt(
        this.predictiveSearchResults
          .querySelector(
            '[data-total-results]'
          )
          ?.dataset.totalResults
      ) || 0
    );
  }

  dispatchSearchUpdateEvent(query) {
    const {
      SearchUpdateEvent
    } =
      window.StandardEvents || {};

    if (!SearchUpdateEvent) {
      return null;
    }

    const deferred =
      SearchUpdateEvent
        .createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query
        },
        promise:
          deferred.promise
      })
    );

    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText
      );
    }

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText;

    setTimeout(() => {
      this.statusElement
        ?.setAttribute(
          'aria-hidden',
          'true'
        );
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute(
      'loading'
    );

    const countElement =
      this.predictiveSearchResults
        .querySelector(
          '[data-predictive-search-live-region-count-value]'
        );

    if (
      countElement &&
      this.statusElement
    ) {
      this.setLiveRegionText(
        countElement.textContent
      );
    }
  }

  renderSearchResults(
    resultsMarkup
  ) {
    this.predictiveSearchResults
      .innerHTML =
        resultsMarkup;

    this.setAttribute(
      'results',
      true
    );

    this.setLiveRegionResults();

    this.open();
  }

  clearPredictiveResults() {
    this.predictiveSearchResults
      .innerHTML = '';

    this.removeAttribute(
      'results'
    );

    this.removeAttribute(
      'loading'
    );

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );
  }

  getResultsMaxHeight() {
    const headerBottom =
      document
        .querySelector(
          '.section-header'
        )
        ?.getBoundingClientRect()
        .bottom || 0;

    this.resultsMaxHeight =
      window.innerHeight -
      headerBottom;

    return this.resultsMaxHeight;
  }

  openSearchHub() {
    this.updateGenderRecommendations();
    this.open();
  }

  open() {
    const searchBody =
      this.querySelector(
        '.search-modal__body'
      );

    if (searchBody) {
      searchBody.style.maxHeight =
        this.resultsMaxHeight ||
        `${this.getResultsMaxHeight()}px`;

      searchBody.style.overflowY =
        'auto';
    }

    this.setAttribute(
      'open',
      true
    );

    this.input.setAttribute(
      'aria-expanded',
      true
    );

    this.isOpen = true;
  }

  close(
    clearSearchTerm = false
  ) {
    this.closeResults(
      clearSearchTerm
    );

    this.isOpen = false;
  }

  closeResults(
    clearSearchTerm = false
  ) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';

      this.clearPredictiveResults();
      this.renderRecentSearches();
    }

    const selected =
      this.querySelector(
        '#predictive-search-results [aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.input.setAttribute(
      'aria-expanded',
      false
    );

    this.resultsMaxHeight = false;

    const searchBody =
      this.querySelector(
        '.search-modal__body'
      );

    if (searchBody) {
      searchBody.removeAttribute(
        'style'
      );
    }
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);